/**
 * The ROUND as a card — the rules, with no React and no imports.
 *
 * A round is one turn of a STANDING member's recurring work: the daily check, the weekly batch. Only a
 * member carrying a `cadence` has any, which is the first and most important rule here — a member with no
 * cadence must render NOTHING, not an empty "Turns" section, because a goal that was never meant to recur
 * has not failed to recur.
 *
 * The three "we have nothing to show" cases are deliberately distinct, and collapsing any two of them is
 * the bug this module exists to prevent:
 *
 *   - **absent**      — not a standing member. There is no such thing as its turns. Render nothing.
 *   - **unavailable** — it IS standing, and we did not learn its turns (the read failed, or the payload
 *                       predates this field). Say so. An empty state here reads as "no turns", which is a
 *                       claim we cannot make — this codebase has already shipped a read failure as an
 *                       empty state once.
 *   - **awaiting**    — it is standing, the read landed, and it has no turns yet. Rounds are brand new and
 *                       nothing has minted one for most members, so this is the ordinary case. It must not
 *                       render as "0 turns done" or any number: absent is not zero, and a count implies
 *                       something was measured.
 *
 * And the fourth: **rounds**. Which turn is running now is a question about `closesAt`, NOT about
 * `status`. `QueryGoalRounds` closes elapsed rounds on its way out, so a row can come back CLOSED because
 * answering the query is what closed it — the stored status is evaluated before the closer runs. The
 * service documents that wart on the RPC; this reads the clock instead.
 *
 * `CLOSED_LATE_AND_SHORT` is NOT a success. It means the cadence got stuck and the deadline stopper closed
 * the turn late, so it takes the DS's one neutral grey — the same "terminal, but never green" treatment
 * `OVERTAKEN` / `CALLED_OFF` / `DEADLINE_PASSED` take in `goal-status.ts`, and for the same reason. Only
 * `SETTLED` is the turn that genuinely finished its work, and only it wears the green tick.
 *
 * Import-free on purpose: `orion-web`'s jest config cannot parse the ESM common-lib pulls in, so a unit
 * test may not touch the app's dependency graph (see the package CLAUDE.md). The enum values are string
 * literals rather than reads off the entities barrel for a second reason — a module-level constant that
 * reads an enum through a barrel evaluates before the barrel initialises and takes an unrelated suite down
 * with a TypeError far from its cause.
 */

/** One turn of a standing member's cadence, as the goals route serves it. */
export interface WebGoalRound {
  id: string;
  /** The turn's idempotency key, derived from the cadence and the clock (e.g. `2026-09-01`). */
  roundKey?: string;
  /** ISO. When the turn began. */
  opensAt?: string;
  /** ISO. The deadline the turn is measured against — and the only honest answer to "is it running". */
  closesAt?: string;
  /** `SETTLED` | `CLOSED_LATE_AND_SHORT`. Absent while the turn is still open, or unrecorded. */
  closure?: string;
  /** ISO. When the turn was actually closed. */
  closedDate?: string;
}

/** What the rounds section should render. See the module comment for why these four are distinct. */
export type RoundsView =
  | { kind: 'absent' }
  | { kind: 'unavailable' }
  | { kind: 'awaiting' }
  | { kind: 'rounds'; current?: WebGoalRound; past: WebGoalRound[] };

/** The rounds-bearing slice of a goal. A member is itself a goal, so this is the goal shape. */
export interface RoundsBearing {
  cadence?: string;
  rounds?: WebGoalRound[];
  roundsUnavailable?: boolean;
}

const CADENCE_LABEL_KEYS: Record<string, string> = {
  DAILY: 'goals.rounds.cadence.daily',
  WEEKLY: 'goals.rounds.cadence.weekly',
  EVENT_DRIVEN: 'goals.rounds.cadence.eventDriven',
};

/**
 * The cadence a member is standing on, when it names one this build knows.
 *
 * `UNKNOWN_CADENCE`, an empty string and a kind added after this build shipped all read as NOT standing —
 * a member is standing iff we can say what its rhythm is. Naming a rhythm we cannot label would put an
 * untranslated enum on screen; claiming it is standing without one would open a section we cannot title.
 */
export function knownCadence(cadence: string | undefined): string | undefined {
  const value = cadence?.trim().toUpperCase();
  return value && value in CADENCE_LABEL_KEYS ? value : undefined;
}

/** The i18n key naming the cadence — "Daily" / "Weekly" / "When something happens". */
export function cadenceLabelKey(cadence: string): string {
  return CADENCE_LABEL_KEYS[cadence] ?? CADENCE_LABEL_KEYS.DAILY;
}

/** A round is still running when its deadline has not passed. Never `status` — see the module comment. */
export function isRoundRunning(round: WebGoalRound, nowMs: number): boolean {
  if (!round.closesAt) return false;
  const closes = Date.parse(round.closesAt);
  return Number.isFinite(closes) && closes > nowMs;
}

function startedAt(round: WebGoalRound): number {
  const value = Date.parse(round.opensAt ?? round.closesAt ?? '');
  return Number.isFinite(value) ? value : 0;
}

/**
 * What to render for this member's rounds.
 *
 * Note the order: `cadence` is asked FIRST, so a member that is not standing renders nothing even if a
 * payload somehow carried rounds or an error flag for it. Standing-ness is the property the section
 * belongs to.
 */
export function resolveRoundsView(goal: RoundsBearing, nowMs: number): RoundsView {
  if (!knownCadence(goal.cadence)) return { kind: 'absent' };
  // A standing member whose rounds we did not learn — the read failed, or this payload predates the
  // field. Either way we do not know that it has none, so we must not draw the empty state.
  if (goal.roundsUnavailable || !goal.rounds) return { kind: 'unavailable' };
  if (goal.rounds.length === 0) return { kind: 'awaiting' };

  const ordered = [...goal.rounds].sort((a, b) => startedAt(b) - startedAt(a));
  const currentIndex = ordered.findIndex((round) => isRoundRunning(round, nowMs));
  const current = currentIndex === -1 ? undefined : ordered[currentIndex];
  const past = ordered.filter((_, i) => i !== currentIndex);
  return { kind: 'rounds', current, past };
}

/** The i18n key for how a closed turn ENDED. An unrecorded closure says "Closed" and claims nothing. */
export function roundClosureLabelKey(closure: string | undefined): string {
  if (closure === 'SETTLED') return 'goals.rounds.settled';
  if (closure === 'CLOSED_LATE_AND_SHORT') return 'goals.rounds.lateAndShort';
  return 'goals.rounds.closed';
}

/**
 * The badge STYLE a closed turn wears — a Style name the token layer resolves, never a colour.
 *
 * Only `SETTLED` is green. `CLOSED_LATE_AND_SHORT` takes the DS's one neutral grey, because the cadence
 * got stuck and the deadline stopper closed the turn: an ending where the work was not delivered, which
 * is the same reading `OVERTAKEN` / `CALLED_OFF` / `DEADLINE_PASSED` get — terminal, never a tick, and
 * not a red failure either.
 */
export function roundClosureVariant(closure: string | undefined): 'done' | 'waiting-on-a-reply' {
  return closure === 'SETTLED' ? 'done' : 'waiting-on-a-reply';
}
